// API utility functions for authentication
import { buildApiUrl, buildDebugExplorerUrl, endpoints, timeout } from '../config/api';
import { shouldUseKivoConfigSource } from './kivoFeature';

/** @typedef {import('./types/api.js').NameApiResponse} NameApiResponse */

// Token management functions
export const getStoredToken = () => {
  // console.log("Getting stored token:", localStorage.getItem('access_token'));
  return localStorage.getItem('access_token');
};

// Wait briefly for a token to be written (e.g. after Kivo OAuth redirect)
const waitForToken = async (timeoutMs = 2000, pollIntervalMs = 100) => {
  const start = Date.now();
  let token = getStoredToken();
  while (!token && Date.now() - start < timeoutMs) {
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    token = getStoredToken();
  }
  return token;
};

export const getStoredRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

export const getStoredUser = () => {
  return localStorage.getItem('user');
};

// Return the current user's role (e.g. 'admin' or 'user') by parsing stored user info.
// Returns null if no user info is available or parsing fails.
export const getUserRole = () => {
  const stored = getStoredUser();
  if (!stored) return null;
  try {
    const user = JSON.parse(stored);
    // Prefer an explicit `role` field, fall back to `is_admin` boolean when present
    if (user.role) return user.role;
    if (typeof user.is_admin === 'boolean') return user.is_admin ? 'admin' : 'user';
    return null;
  } catch (err) {
    console.error('Failed to parse stored user for role:', err);
    return null;
  }
};

// Return the current user's type: 'admin', 'intrusion', or null
// Returns 'admin' if is_admin === true
// Returns 'intrusion' if is_admin === false AND is_intrusion === true
// Returns null otherwise
export const getUserType = () => {
  const stored = getStoredUser();
  let user = null;
  
  if (stored) {
    try {
      user = JSON.parse(stored);
    } catch (err) {
      console.error('Failed to parse stored user for type:', err);
    }
  }
  
  // Fallback to token if user not in localStorage or parsing failed
  if (!user) {
    user = getUserFromToken();
  }
  
  if (!user) {
    return null;
  }
  
  // Handle different boolean representations (true/false, 1/0, "true"/"false")
  const isAdmin = user.is_admin === true || user.is_admin === 1 || user.is_admin === "true" || user.is_admin === "1";
  const isIntrusion = user.is_intrusion === true || user.is_intrusion === 1 || user.is_intrusion === "true" || user.is_intrusion === "1";
  
  if (isAdmin) {
    return 'admin';
  }
  
  if (!isAdmin && isIntrusion) {
    return 'intrusion';
  }
  
  return null;
};

// Decode a JWT token payload (browser-safe). Returns payload object or null.
export const parseJwt = (token) => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    // Base64url -> Base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Pad base64 string
    const pad = base64.length % 4;
    const padded = base64 + (pad ? '='.repeat(4 - pad) : '');
    const json = window.atob(padded);
    return JSON.parse(json);
  } catch (err) {
    console.error('Failed to parse JWT:', err);
    return null;
  }
};

// Derive a user object (including role) from the access token if available.
export const getUserFromToken = () => {
  const token = getStoredToken();
  if (!token) return null;
  const payload = parseJwt(token);
  if (!payload) return null;

  // Attempt to map common claim names to a role or admin flag
  const roleClaim = payload.role || payload.roles || payload.scope || payload.scopes;
  let role = null;

  if (typeof roleClaim === 'string') {
    role = roleClaim;
  } else if (Array.isArray(roleClaim) && roleClaim.length > 0) {
    // pick first role string
    role = roleClaim[0];
  } else if (typeof payload.is_admin === 'boolean') {
    role = payload.is_admin ? 'admin' : 'user';
  } else if (payload.admin === true) {
    role = 'admin';
  }

  // Return a minimal user object so existing code that reads localStorage user will behave
  const user = { ...payload };
  if (role) user.role = role;
  return user;
};

export const storeTokens = (accessToken, refreshToken = null) => {
  localStorage.setItem('access_token', accessToken);
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
};

export const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
};

export const isAuthenticated = () => {
  return !!getStoredToken();
};

export const LOGOUT_REASON_STORAGE_KEY = 'auth_logout_reason';

let refreshPromise = null;
let sessionRedirectStarted = false;

const readErrorResponse = async (response) => {
  const body = await response.clone().json().catch(() => ({}));
  const detail = body?.detail;
  const code = detail && typeof detail === 'object' && !Array.isArray(detail)
    ? detail.code || null
    : null;
  let message = null;

  if (typeof detail === 'string') {
    message = detail;
  } else if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    message = detail.message || detail.msg || null;
    if (!message) {
      try {
        message = JSON.stringify(detail);
      } catch (_) {
        message = null;
      }
    }
  } else if (Array.isArray(detail)) {
    message = detail
      .map((entry) => (
        entry?.msg
          ? entry.msg.replace('Value error, ', '')
          : JSON.stringify(entry)
      ))
      .join(', ');
  }

  return { body, detail, code, message };
};

const createResponseError = (response, errorData = {}) => {
  const err = new Error(
    errorData.message || `HTTP error! status: ${response.status}`
  );
  err.status = response.status;
  err.code = errorData.code || null;
  err.detail = errorData.detail;
  err.body = errorData.body;
  return err;
};

const endBrowserSession = (reason, message) => {
  clearTokens();
  sessionStorage.setItem(
    LOGOUT_REASON_STORAGE_KEY,
    JSON.stringify({ reason, message })
  );

  if (!sessionRedirectStarted && window.location.pathname !== '/login') {
    sessionRedirectStarted = true;
    window.location.assign('/login');
  }
};

export const terminateBrowserSession = (reason, message) => {
  endBrowserSession(reason, message);
};

const refreshStoredTokens = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      const err = new Error('No refresh token found');
      err.code = 'SESSION_EXPIRED';
      throw err;
    }

    const response = await fetch(buildApiUrl(endpoints.auth.refresh), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      const errorData = await readErrorResponse(response);
      throw createResponseError(response, errorData);
    }

    const data = await response.json();
    storeTokens(data.access_token, data.refresh_token);
    return data.access_token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// API call functions
export const login = async (email, password) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(buildApiUrl(endpoints.auth.login), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await readErrorResponse(response);
      throw createResponseError(response, errorData);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
};

export const kivoLogin = async (email, password, rememberMe = false) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(buildApiUrl('/auth/kivo-login'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        remember_me: rememberMe,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
};

// Kivo OAuth2 functions
export const getKivoSignInUrl = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(buildApiUrl('/auth/kivo-sign-in'), {
      method: 'GET',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
};

// User Management API functions
export const getUsers = async (limit = 10, offset = 0, q = '', isActive = true) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    is_active: String(Boolean(isActive)),
  });
  if (q && q.trim()) {
    params.set('q', q.trim());
  }
  const response = await authenticatedFetch(`/auth/admin/users?${params.toString()}`);
  return response.json();
};

export const getAllUsernames = async (q = '', isActive = true) => {
  const params = new URLSearchParams({
    is_active: String(Boolean(isActive)),
  });
  if (q && q.trim()) {
    params.set('q', q.trim());
  }
  const response = await authenticatedFetch(`/auth/admin/users/names?${params.toString()}`);
  return response.json();
};

export const getUserById = async (userId) => {
  const response = await authenticatedFetch(`/auth/admin/users/${userId}`);
  return response.json();
};

export const activateUser = async (userId) => {
  const response = await authenticatedFetch(`/auth/admin/users/${userId}/activate`, {
    method: 'POST',
  });
  return response;
};

export const deactivateUser = async (userId) => {
  const response = await authenticatedFetch(`/auth/admin/users/${userId}/deactivate`, {
    method: 'POST',
  });
  return response;
};

export const resetUserPassword = async (userId, newPassword) => {
  const response = await authenticatedFetch(`/auth/admin/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({
      new_password: newPassword,
    }),
  });
  return response;
};

export const registerUser = async (userData) => {
  try {
    const response = await authenticatedFetch('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return response;
  } catch (error) {
    if (error.message === 'No authentication token found') {
      const response = await fetch(buildApiUrl('/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      return response;
    }
    throw error;
  }
};

export const updateAdminStatus = async (userId, isAdmin) => {
  const response = await authenticatedFetch(`/auth/admin/users/${userId}/admin-status`, {
    method: 'PUT',
    body: JSON.stringify({
      is_admin: isAdmin,
    }),
  });
  return response;
};

export const updateIntrusionStatus = async (userId, isIntrusion) => {
  const response = await authenticatedFetch(`/auth/admin/users/${userId}/intrusion-status`, {
    method: 'PUT',
    body: JSON.stringify({
      is_intrusion: isIntrusion,
    }),
  });
  return response;
};

export const updateSuperAdminStatus = async (userId, isSuperAdmin) => {
  const response = await authenticatedFetch(`/auth/super-admin/users/${userId}/super-admin-status`, {
    method: 'PUT',
    body: JSON.stringify({
      is_super_admin: isSuperAdmin,
    }),
  });
  return response;
};

export const forceLogoutUser = async (userId) => {
  const response = await authenticatedFetch(`/auth/super-admin/force-logout/${userId}`, {
    method: 'POST',
  });
  return response;
};

export const forceLogoutEveryone = async () => {
  const response = await authenticatedFetch('/auth/super-admin/force-logout-all', {
    method: 'POST',
  });
  return response;
};

export const getAccessControlUsers = async (limit = 10, offset = 0, q = '', isActive = true) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    is_active: String(Boolean(isActive)),
  });
  if (q && q.trim()) {
    params.set('q', q.trim());
  }
  const response = await authenticatedFetch(`/auth/super-admin/users/access-control?${params.toString()}`);
  return response.json();
};

export const updateUserNotificationsEnabled = async (userId, enabled) => {
  const response = await authenticatedFetch(`/auth/super-admin/users/${userId}/notifications-enabled`, {
    method: 'PUT',
    body: JSON.stringify({ notifications_enabled: enabled }),
  });
  return response.json();
};

export const updateUserDashboardWsStatus = async (userId, enabled) => {
  const response = await authenticatedFetch(`/auth/super-admin/users/${userId}/dashboard-ws-status`, {
    method: 'PUT',
    body: JSON.stringify({ dashboard_ws_enabled: enabled }),
  });
  return response.json();
};

export const getCurrentUser = async () => {
  const response = await authenticatedFetch('/auth/me', {
    method: 'GET',
  });
  if (response.ok) {
    return await response.json();
  }
  throw new Error('Failed to get current user');
};

// Get user permissions, allowed routes, and menu items from backend
export const getUserPermissions = async () => {
  const response = await authenticatedFetch('/auth/permissions', {
    method: 'GET',
  });
  if (response.ok) {
    const data = await response.json();
    console.log('Permissions API Response Menu Items', data?.menu_items || []);
    return data;
  }
  throw new Error('Failed to get user permissions');
};

// Faces API functions
export const getFaces = async (limit = 10, offset = 0) => {
  const response = await authenticatedFetch(`/faces/?limit=${limit}&offset=${offset}`);
  return response.json();
};

export const getDeletedFaces = async (limit = 10, offset = 0) => {
  const response = await authenticatedFetch(`/faces/deleted?limit=${limit}&offset=${offset}`);
  return response.json();
};

export const getPersons = async (
  limit = 10,
  offset = 0,
  includeDeleted = false,
  q = null,
  personId = null,
  companyId = null,
  branchId = null,
  isIntrusion = false,
  designation = null,
  department = null,
  isVendor = null,
  isVisitor = null
) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  params.append('include_deleted', includeDeleted === true ? 'true' : 'false');
  params.append('is_intrusion', isIntrusion === true ? 'true' : 'false');
  if (q) params.append('q', q);
  if (personId) params.append('person_id', personId);
  if (companyId) params.append('company_id', companyId);
  if (branchId) params.append('branch_id', branchId);
  if (designation) params.append('designation', designation);
  if (department) params.append('department', department);
  if (typeof isVendor === 'boolean') {
    params.append('is_vendor', String(isVendor));
  }
  if (typeof isVisitor === 'boolean') {
    params.append('is_visitor', String(isVisitor));
  }
  const response = await authenticatedFetch(`/persons/?${params.toString()}`);
  return response.json();
};

// Get Export Fields from backend
export const getPeopleExportFields = async ()=>{
  const lastWord = window.location.pathname.split("/").filter(Boolean).pop();
  const response = await authenticatedFetch(`/persons/export-meta?people=${lastWord}`);
  return response.json();
}

export const exportPeople = async (fields, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, String(value));
    }
  });
  const response = await authenticatedFetch(`/persons/export?${params.toString()}`, {
    method: 'POST',
    body: JSON.stringify({ fields }),
  });
  return response.json();
};

/** Vendor directory: same response shape as getPersons, server forces vendor-only rows. */
export const getVendorPersons = async (
  limit = 10,
  offset = 0,
  includeDeleted = false,
  q = null,
  personId = null,
  companyId = null,
  branchId = null,
  isIntrusion = false,
  designation = null,
  department = null
) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  params.append('include_deleted', includeDeleted === true ? 'true' : 'false');
  params.append('is_intrusion', isIntrusion === true ? 'true' : 'false');
  if (q) params.append('q', q);
  if (personId) params.append('person_id', personId);
  if (companyId) params.append('company_id', companyId);
  if (branchId) params.append('branch_id', branchId);
  if (designation) params.append('designation', designation);
  if (department) params.append('department', department);
  const response = await authenticatedFetch(`/persons/vendor?${params.toString()}`);
  return response.json();
};

/** Visitor directory: same response shape as getVendorPersons. */
export const getVisitorPersons = async (
  limit = 10,
  offset = 0,
  includeDeleted = false,
  q = null,
  personId = null,
  companyId = null,
  branchId = null,
  isIntrusion = false,
  designation = null,
  department = null
) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  params.append('include_deleted', includeDeleted === true ? 'true' : 'false');
  params.append('is_intrusion', isIntrusion === true ? 'true' : 'false');
  if (q) params.append('q', q);
  if (personId) params.append('person_id', personId);
  if (companyId) params.append('company_id', companyId);
  if (branchId) params.append('branch_id', branchId);
  if (designation) params.append('designation', designation);
  if (department) params.append('department', department);
  const response = await authenticatedFetch(`/persons/visitor?${params.toString()}`);
  return response.json();
};

/**
 * Single person for detail routes: tries GET /persons with is_vendor=false, then GET /persons/vendor.
 */
export const getPersonById = async (personId) => {
  const pid = personId != null ? String(personId).trim() : '';
  if (!pid) return null;
  let data = await getPersons(1, 0, false, null, pid, null, null, false, null, null, false, false);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  data = await getVendorPersons(1, 0, false, null, pid);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  data = await getVisitorPersons(1, 0, false, null, pid);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  data = await getIntrusionPersons(1, 0, false, null, pid);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  return null;
};

/** Single vendor for detail routes: tries GET /persons/vendor, then GET /persons with is_vendor=false. */
export const getVendorById = async (personId) => {
  const pid = personId != null ? String(personId).trim() : '';
  if (!pid) return null;
  let data = await getVendorPersons(1, 0, false, null, pid);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  data = await getPersons(1, 0, false, null, pid, null, null, false, null, null, false, false);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  return null;
};

export const getVisitorById = async (personId) => {
  const pid = personId != null ? String(personId).trim() : '';
  if (!pid) return null;
  let data = await getVisitorPersons(1, 0, false, null, pid);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  data = await getPersons(1, 0, false, null, pid, null, null, false, null, null, false, false);
  if (data?.persons?.length) {
    return data.persons[0];
  }
  return null;
};

export const getIntrusionPersons = async (limit = 10, offset = 0, includeDeleted = false, q = null, personId = null, companyId = null) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  params.append('include_deleted', includeDeleted === true ? 'true' : 'false');
  if (q) params.append('q', q);
  if (personId) params.append('person_id', personId);
  if (companyId) params.append('company_id', companyId);
  const response = await authenticatedFetch(`/persons/intrusion?${params.toString()}`);
  return response.json();
};

/**
 * Get all person name for suggestions
 * ----------------------------------
 * 
 * @param {boolean} includeDeleted 
 * @param {string | null} companyId 
 * @param {boolean} isIntrusion 
 * @param {*} extraFilters 
 * @returns {Promise<NameApiResponse>}
 */
export const getAllPersonNames = async (
  includeDeleted = false,
  companyId = null,
  isIntrusion = false,
  extraFilters = {}
) => {
  const params = new URLSearchParams();
  params.append('include_deleted', includeDeleted === true ? 'true' : 'false');
  params.append('is_intrusion', isIntrusion === true ? 'true' : 'false');
  if (companyId) params.append('company_id', companyId);
  if (extraFilters.branchId) params.append('branch_id', extraFilters.branchId);
  if (extraFilters.designation) params.append('designation', extraFilters.designation);
  if (extraFilters.department) params.append('department', extraFilters.department);
  if (typeof extraFilters.isVendor === 'boolean') {
    params.append('is_vendor', String(extraFilters.isVendor));
  }
  if (typeof extraFilters.isVisitor === 'boolean') {
    params.append('is_visitor', String(extraFilters.isVisitor));
  }
  const response = await authenticatedFetch(`/persons/names?${params.toString()}`);
  return response.json();
};



export const getFaceById = async (faceId) => {
  const response = await authenticatedFetch(`/faces/${faceId}`);
  return response.json();
};

export const getPersonFaces = async (personId) => {
  try {
    const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}`);
    return response.json();
  } catch (error) {
    if (error.message && (error.message.includes('404') || error.message.toLowerCase().includes('not found'))) {
      return [];
    }
    throw error;
  }
};

export const updatePersonName = async (personId, name) => {
  const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}/name`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
  return response.json();
};

export const updatePersonDetails = async (
  personId,
  personIdNew,
  companyId,
  branchId,
  designation = undefined,
  department = undefined,
  employeeCode = undefined,
) => {
  const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}/details`, {
    method: 'PATCH',
    body: JSON.stringify({ 
      person_id: personIdNew,
      company_id: companyId,
      branch_id: branchId,
      designation,
      department,
      employee_code: employeeCode,
    }),
  });
  return response.json();
};

/** Toggle employee vs intrusion profile for all active faces of a person (admin UI). */
export const updatePersonIntrusionProfile = async (
  personId,
  { is_intrusion, company_id, branch_id } = {}
) => {
  const body = { is_intrusion: !!is_intrusion };
  if (is_intrusion && company_id) {
    body.company_id = company_id;
  }
  if (!is_intrusion) {
    if (company_id) {
      body.company_id = company_id;
      body.branch_id =
        branch_id !== undefined && branch_id !== null && String(branch_id).trim()
          ? String(branch_id).trim()
          : null;
    }
    // Demote without employee org: omit company/branch so vendor/visitor switches
    // are not treated as employee profile assignment on the server.
  }
  const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}/intrusion-profile`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return response.json();
};

/** Toggle employee vs vendor role for all active faces of a person (admin UI). */
export const updatePersonVendorProfile = async (
  personId,
  { is_vendor, company_id, branch_id } = {}
) => {
  const body = { is_vendor: !!is_vendor };
  if (company_id) {
    body.company_id = String(company_id).trim();
    body.branch_id =
      branch_id !== undefined && branch_id !== null && String(branch_id).trim()
        ? String(branch_id).trim()
        : null;
  }
  const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}/vendor-profile`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return response.json();
};

export const updatePersonVisitorProfile = async (
  personId,
  { is_visitor, company_id, branch_id } = {}
) => {
  const body = { is_visitor: !!is_visitor };
  if (company_id) {
    body.company_id = String(company_id).trim();
    body.branch_id =
      branch_id !== undefined && branch_id !== null && String(branch_id).trim()
        ? String(branch_id).trim()
        : null;
  }
  const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}/visitor-profile`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return response.json();
};

export const softDeleteFace = async (faceId) => {
  const response = await authenticatedFetch(`/faces/${faceId}`, {
    method: 'DELETE',
  });
  return response;
};

export const registerFaceBase64 = async (
  name,
  personId,
  imageBase64,
  imageFormat = 'jpeg',
  angle = null,
  customAngleLabel = null,
  companyId = null,
  branchId = null,
  isIntrusion = false,
  designation = null,
  department = null,
  restrictedCameraIds = null,
  employeeType = null,
  employeeCode = null,
  comment
) => {
  const response = await authenticatedFetch('/faces/register-base64', {
    method: 'POST',
    body: JSON.stringify({
      name,
      person_id: personId || undefined,
      image_base64: imageBase64,
      image_format: imageFormat,
      angle: angle || undefined,
      custom_angle_label: customAngleLabel || undefined,
      designation: designation || undefined,
      department: department || undefined,
      company_id: companyId || undefined,
      branch_id: branchId || undefined,
      is_intrusion: isIntrusion,
      restricted_camera_ids: Array.isArray(restrictedCameraIds) ? restrictedCameraIds : undefined,
      employee_type: employeeType || undefined,
      employee_code: employeeCode || undefined,
      comment
    }),
  });
  return response.json();
};

export const updatePersonCameraAccess = async (personId, restrictedCameraIds = []) => {
  const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}/camera-access`, {
    method: 'PATCH',
    body: JSON.stringify({
      restricted_camera_ids: Array.isArray(restrictedCameraIds) ? restrictedCameraIds : [],
    }),
  });
  return response.json();
};

export const updatePersonSimilarityThreshold = async (personId, similarityThreshold = null) => {
  const response = await authenticatedFetch(
    `/faces/person/${encodeURIComponent(personId)}/similarity-threshold`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        similarity_threshold: similarityThreshold,
      }),
    }
  );
  return response.json();
};

export const getSystemSimilarityThreshold = async () => {
  const response = await authenticatedFetch('/settings/similarity-threshold');
  return response.json();
};

export const verifyFaceQualityBase64 = async (
  imageBase64,
  referenceImageBase64 = null,
  similarityThreshold = null
) => {
  const response = await authenticatedFetch('/faces/verify-quality-base64', {
    method: 'POST',
    body: JSON.stringify({
      image_base64: imageBase64,
      reference_image_base64: referenceImageBase64 || undefined,
      similarity_threshold: similarityThreshold ?? undefined,
    }),
  });
  return response.json();
};

export const getFaceImage = async (faceId) => {
  const response = await authenticatedFetch(`/faces/${faceId}/image`, { timeoutMs: 30000 });
  return response.blob(); // Return blob for image data
};

export const restoreFace = async (faceId) => {
  const response = await authenticatedFetch(`/faces/${faceId}/restore`, {
    method: 'POST',
  });
  return response.json();
};

export const updateFaceImage = async (faceId, file) => {
  const token = getStoredToken();
  if (!token) throw new Error('No authentication token found');

  const formData = new FormData();
  formData.append('file', file);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(buildApiUrl(`/faces/${faceId}/image`), {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Do NOT set Content-Type; browser will set multipart boundary
      },
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
};

// Audit API functions (admin)
export const getAuditConfig = async () => {
  const response = await authenticatedFetch('/auth/admin/audit/config');
  return response.json();
};

export const updateAuditConfig = async (updates) => {
  const response = await authenticatedFetch('/auth/admin/audit/config', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return response.json();
};

export const getAuditStats = async (days = 30) => {
  const response = await authenticatedFetch(`/auth/admin/audit/stats?days=${days}`);
  return response.json();
};

export const getAuditLogs = async ({ operation_type = '', user_id = '', person_id = '', employee_code = '', start_time = '', end_time = '', name = '', camera = '', camera_id = '', status = '', audit_id = '', is_intrusion = null, is_vendor = null, is_visitor = null, bucketize = null, greater_than_total_time = null, vision_review_result = '', days = 30, limit = 10, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (operation_type) params.append('operation_type', operation_type);
  if (user_id) params.append('user_id', user_id);
  if (person_id) params.append('person_id', person_id);
  if (employee_code) params.append('employee_code', employee_code);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (name) params.append('name', name);
  if (camera) params.append('camera', camera);
  if (camera_id) {
    const cameraIds = Array.isArray(camera_id) ? camera_id : [camera_id];
    const joined = cameraIds.map((id) => String(id || '').trim()).filter(Boolean).join(',');
    if (joined) params.append('camera_id', joined);
  }
  if (status) params.append('status', status);
  if (vision_review_result) params.append('vision_review_result', vision_review_result);
  if (audit_id) params.append('audit_id', audit_id);
  if (is_intrusion !== null && is_intrusion !== undefined) {
    params.append('is_intrusion', String(is_intrusion).toLowerCase());
  }
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  if (bucketize !== null && bucketize !== undefined) params.append('bucketize', String(Boolean(bucketize)));
  if (greater_than_total_time != null && greater_than_total_time !== '' && Number(greater_than_total_time) > 0) {
    params.append('greater_than_total_time', String(Math.trunc(Number(greater_than_total_time))));
  }
  if (days != null) params.append('days', String(days));
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  
  const url = `/auth/admin/audit/logs?${params.toString()}`;
  const response = await authenticatedFetch(url);
  return response.json();
};

export const getAuditLogDetail = async (auditId, { include_bucket = true } = {}) => {
  const params = new URLSearchParams();
  if (include_bucket) params.append('include_bucket', 'true');
  const qs = params.toString();
  const url = `/auth/admin/audit/logs/${encodeURIComponent(auditId)}${qs ? `?${qs}` : ''}`;
  const response = await authenticatedFetch(url);
  return response.json();
};

export const cleanupAuditLogs = async () => {
  const response = await authenticatedFetch('/auth/admin/audit/cleanup', { method: 'POST' });
  return response.json();
};

/** True when HLS manifest path is present and status allows HLS playback (matches /stream-url HLS branch). */
export function isRecordingHlsStreamable(rec) {
  if (!rec) return false;
  const status = (rec.hls_status || '').toString().trim().toLowerCase();
  const path = rec.hls_playlist_path;
  const hlsUsable = status === 'ready' || status === 'partial';
  return hlsUsable && typeof path === 'string' && path.trim().length > 0;
}

/**
 * True when the recordings UI should allow opening the player (blocks MKV-only while HLS is pending/processing).
 */
export function isRecordingWebPlayable(rec) {
  if (!rec) return false;
  const status = (rec.hls_status || '').toString().trim().toLowerCase();
  if (status === 'pending' || status === 'processing') return false;
  if (isRecordingHlsStreamable(rec)) return true;
  if (rec.file_path && String(rec.file_path).trim()) return true;
  return false;
}

/** Tooltip when play is blocked because FFmpeg is still running. */
export function recordingTranscodingBlockedTooltip(rec) {
  const status = (rec?.hls_status || '').toString().trim().toLowerCase();
  if (status === 'pending' || status === 'processing') {
    return 'Transcoding in progress — playback will be available shortly.';
  }
  return '';
}

/**
 * Resolve recording + seek offset for audit log full playback (same stream as Recordings page).
 * HTTP errors are thrown by {@link authenticatedFetch} (not returned as a parsed body).
 * @returns {Promise<{ recording: object, seek_seconds: number, notice?: string|null }>}
 * @throws {Error} On timeout, HTTP error, or non-JSON / invalid JSON body
 */
export const fetchAuditRecordingPlaybackInfo = async (auditId) => {
  try {
    const response = await authenticatedFetch(
      `/recording/recordings/audit-playback?audit_id=${encodeURIComponent(auditId)}`,
      { timeoutMs: 60000 },
    );
    const data = await response.json().catch(() => {
      throw new Error('Invalid response from audit playback');
    });
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error('Invalid audit playback payload');
    }
    return data;
  } catch (error) {
    if (error && error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
};

/**
 * Request a signed MKV download URL for a recording clip, then trigger browser save.
 * Uses token URL so large files stream to disk without loading into JS memory.
 *
 * @param {string} recordingId
 * @param {{ startSeconds?: number, endSeconds?: number|null }} range
 * @returns {Promise<{ filename: string }>}
 */
export const downloadRecordingClip = async (recordingId, { startSeconds = 0, endSeconds = null } = {}) => {
  const params = new URLSearchParams();
  params.set('start_seconds', String(Math.max(0, startSeconds)));
  if (endSeconds != null && Number.isFinite(endSeconds)) {
    params.set('end_seconds', String(endSeconds));
  }

  const response = await authenticatedFetch(
    `/recording/recordings/${encodeURIComponent(recordingId)}/download-url?${params.toString()}`,
    { timeoutMs: 120000 },
  );

  if (!response.ok) {
    let detail = 'Download failed';
    try {
      const errBody = await response.json();
      detail = errBody.detail || detail;
    } catch {
      /* ignore */
    }
    throw new Error(typeof detail === 'string' ? detail : 'Download failed');
  }

  const data = await response.json();
  if (!data?.url) {
    throw new Error('Invalid download URL response');
  }

  const filename = data.filename || 'recording.mkv';
  const downloadHref = buildApiUrl(data.url);
  const clipSeconds =
    Number(data.end_seconds) > Number(data.start_seconds)
      ? Number(data.end_seconds) - Number(data.start_seconds)
      : 0;

  // Prefetch so API errors surface in the UI instead of opening a JSON error tab.
  const fileRes = await fetch(downloadHref, { method: 'GET', credentials: 'omit' });
  if (!fileRes.ok) {
    let detail = 'Download failed';
    try {
      const errBody = await fileRes.json();
      detail = errBody.detail || detail;
    } catch {
      /* ignore */
    }
    throw new Error(typeof detail === 'string' ? detail : 'Download failed');
  }

  // Short clips: save via blob URL. Long/full recordings: object URL still streams from blob
  // but for very large files the browser may use disk-backed blob storage.
  const blob = await fileRes.blob();
  if (!blob || blob.size <= 0) {
    throw new Error('Download produced an empty file');
  }
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.setAttribute('download', filename);
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);

  return { filename, clipSeconds };
};

export const getAuditImageBlob = async (audit_id) => {
  const response = await authenticatedFetch(`/auth/admin/audit/recognition/${audit_id}/image`, { timeoutMs: 30000 });
  // authenticatedFetch returns a Response; convert to blob
  const blob = await response.blob();
  return blob;
};

export const getUnmatchedGroupsToday = async ({
  date = '',
  start_date = '',
  end_date = '',
  start_time = '',
  end_time = '',
  limit = 12,
  offset = 0,
} = {}) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const qs = params.toString();
  const response = await authenticatedFetch(`/auth/admin/audit/unmatched/groups/today${qs ? `?${qs}` : ''}`);
  return response.json();
};

export const getAttendanceImageBlob = async (attendance_id) => {
  const response = await authenticatedFetch(`/auth/admin/audit/logs/${attendance_id}/image`, { timeoutMs: 30000 });
  const blob = await response.blob();
  return blob;
};

export const exportActivityLogs = async ({
  start_time = '',
  end_time = '',
  person_id = '',
  person_name = '',
  camera_id = '',
  is_unknown = null,
  status = '',
  is_vendor = null,
  is_visitor = null,
  company_id = '',
  branch_id = '',
  selected_fields = null,
  export_format = 'csv',
  file_name = 'activity_logs',
} = {}) => {
  const response = await authenticatedFetch('/auth/admin/activity/export', {
    method: 'POST',
    timeoutMs: 300000,
    body: JSON.stringify({
      start_time,
      end_time,
      person_id: person_id || undefined,
      person_name: person_name || undefined,
      camera_id: camera_id || undefined,
      is_unknown,
      status: status || undefined,
      is_vendor,
      is_visitor,
      company_id: company_id || undefined,
      branch_id: branch_id || undefined,
      selected_fields,
      export_format,
      file_name,
    }),
  });

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition') || response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : null;
  return { blob, filename };
};

export const deleteAttendanceLog = async (attendance_id, audit_id = '') => {
  const qs = audit_id ? `?audit_id=${encodeURIComponent(audit_id)}` : '';
  const response = await authenticatedFetch(`/auth/admin/attendance/logs/${attendance_id}${qs}`, { method: 'DELETE' });
  return response.json();
};

export const revokeAttendanceByAudit = async (audit_id) => {
  const response = await authenticatedFetch(`/auth/admin/attendance/revoke-by-audit/${encodeURIComponent(audit_id)}`, {
    method: 'POST',
  });
  return response.json();
};

export const exportAuditLogs = async ({ operation_type = '', user_id = '', person_id = '', employee_code = '', start_time = '', end_time = '', name = '', camera = '', camera_id = '', status = '', days = 30, is_intrusion = null, is_vendor = null, is_visitor = null } = {}) => {
  const params = new URLSearchParams();
  if (operation_type) params.append('operation_type', operation_type);
  if (user_id) params.append('user_id', user_id);
  if (person_id) params.append('person_id', person_id);
  if (employee_code) params.append('employee_code', employee_code);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (name) params.append('name', name);
  if (camera) params.append('camera', camera);
  if (camera_id) params.append('camera_id', camera_id);
  if (status) params.append('status', status);
  if (days != null) params.append('days', String(days));
  if (is_intrusion !== null) params.append('is_intrusion', String(is_intrusion));
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  const response = await authenticatedFetch(`/auth/admin/audit/export?${params.toString()}`);
  return response.json();
};

export const formatUnknownMatchLogsForExport = async (logs) => {
  const response = await authenticatedFetch('/auth/admin/audit/unknown-match/format-logs-for-export', {
    method: 'POST',
    body: JSON.stringify({ logs }),
  });
  return response.json();
};

export const getUniqueAuditNames = async (days = 30) => {
  const response = await authenticatedFetch(`/auth/admin/audit/names?days=${days}`);
  return response.json();
};

export const startUnknownMatchJob = async ({ image_base64, start_time = '', end_time = '', name = '', camera = '', similarity_threshold = null} = {}) => {
  const response = await authenticatedFetch('/auth/admin/audit/unknown-match-jobs', {
    method: 'POST',
    body: JSON.stringify({
      image_base64,
      start_time: start_time || undefined,
      end_time: end_time || undefined,
      name: name || undefined,
      camera: camera || undefined,
      similarity_threshold: similarity_threshold ?? undefined,
    }),
  });
  return response.json();
};

export const getUnknownMatchJob = async (jobId) => {
  const response = await authenticatedFetch(`/auth/admin/audit/unknown-match-jobs/${encodeURIComponent(jobId)}`);
  return response.json();
};

export const startVisionReviewJob = async (payload = {}) => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-jobs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const getVisionReviewJob = async (jobId) => {
  const response = await authenticatedFetch(`/auth/admin/audit/vision-review-jobs/${encodeURIComponent(jobId)}`);
  return response.json();
};

export const getVisionReviewSettings = async () => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-settings');
  return response.json();
};

export const updateVisionReviewSettings = async (payload = {}) => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const startVisionReviewBatchJob = async (payload = {}) => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-batch-jobs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const listVisionReviewBatchJobs = async () => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-batch-jobs');
  return response.json();
};

export const getVisionReviewBatchJob = async (batchJobId) => {
  const response = await authenticatedFetch(
    `/auth/admin/audit/vision-review-batch-jobs/${encodeURIComponent(batchJobId)}`
  );
  return response.json();
};

export const cancelVisionReviewBatchJob = async (batchJobId) => {
  const response = await authenticatedFetch(
    `/auth/admin/audit/vision-review-batch-jobs/${encodeURIComponent(batchJobId)}/cancel`,
    { method: 'POST' }
  );
  return response.json();
};

export const cancelVisionReviewJob = async (jobId) => {
  const response = await authenticatedFetch(
    `/auth/admin/audit/vision-review-jobs/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' }
  );
  return response.json();
};

export const runVisionReviewScheduledNow = async () => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-run-scheduled', {
    method: 'POST',
  });
  return response.json();
};

export const getVisionReviewBatchAlertSettings = async () => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-settings');
  const data = await response.json();
  return {
    enabled: Boolean(data.batch_alert_enabled),
    recipients: data.batch_alert_recipients || [],
    mail_subject: data.batch_alert_mail_subject,
    mail_body: data.batch_alert_mail_body,
    email_delivery_ready: Boolean(data.batch_alert_email_delivery_ready),
  };
};

export const updateVisionReviewBatchAlertSettings = async (payload = {}) => {
  const response = await authenticatedFetch('/auth/admin/audit/vision-review-settings', {
    method: 'PUT',
    body: JSON.stringify({
      batch_alert_enabled: payload.enabled,
      batch_alert_recipients: payload.recipients,
      batch_alert_mail_subject: payload.mail_subject,
      batch_alert_mail_body: payload.mail_body,
    }),
  });
  const data = await response.json();
  return {
    enabled: Boolean(data.batch_alert_enabled),
    recipients: data.batch_alert_recipients || [],
    mail_subject: data.batch_alert_mail_subject,
    mail_body: data.batch_alert_mail_body,
    email_delivery_ready: Boolean(data.batch_alert_email_delivery_ready),
  };
};

export const getVisionReviewStats = async ({ start_date, end_date } = {}) => {
  const params = new URLSearchParams();
  if (start_date) params.append('start_date', start_date);
  if (end_date) params.append('end_date', end_date);
  const query = params.toString();
  const response = await authenticatedFetch(
    `/auth/admin/audit/vision-review-stats${query ? `?${query}` : ''}`
  );
  return response.json();
};

export const findPersonCombined = async ({
  image_base64,
  start_time = '',
  end_time = '',
  name = '',
  camera = '',
  similarity_threshold = null,
  max_candidates = 9000,
  limit = 100,
  offset = 0,
} = {}) => {
  const response = await authenticatedFetch('/auth/admin/audit/find-person', {
    method: 'POST',
    body: JSON.stringify({
      image_base64,
      start_time: start_time || undefined,
      end_time: end_time || undefined,
      name: name || undefined,
      camera: camera || undefined,
      similarity_threshold: similarity_threshold ?? undefined,
      max_candidates: max_candidates ?? undefined,
      limit: limit ?? undefined,
      offset: offset ?? undefined,
    }),
  });
  return response.json();
};

export const findPersonActivity = async ({
  image_base64,
  start_time = '',
  end_time = '',
  name = '',
  camera = '',
  similarity_threshold = null,
  max_candidates = 9000,
  limit = 100,
  offset = 0,
} = {}) => {
  const response = await authenticatedFetch('/auth/admin/activity/find-person', {
    method: 'POST',
    body: JSON.stringify({
      image_base64,
      start_time: start_time || undefined,
      end_time: end_time || undefined,
      name: name || undefined,
      camera: camera || undefined,
      similarity_threshold: similarity_threshold ?? undefined,
      max_candidates: max_candidates ?? undefined,
      limit: limit ?? undefined,
      offset: offset ?? undefined,
    }),
  });
  return response.json();
};

export const unmatchToMatch = async (auditIds, personId, personName, similarityThreshold) => {
  const response = await authenticatedFetch('/auth/admin/audit/unmatch-to-match', {
    method: 'POST',
    body: JSON.stringify({
      audit_ids: auditIds,
      person_id: personId,
      person_name: personName || undefined,
      employee_code: undefined, // Will be fetched by backend if not provided
      similarity_threshold: similarityThreshold,
    }),
  });
  return response.json();
};

export const exportAttendanceLogs = async ({
  person_id = '',
  person_name = '',
  action = '',
  start_time = '',
  end_time = '',
  days = 30,
  premises_filter = '',
  late_filter = false,
  include_hidden = false,
  selected_fields = [],
  selected_person_ids = [],
  include_all_employees_presence = false,
  meta_only = false,
  is_intrusion = false,
  is_vendor = null,
  is_visitor = null,
} = {}) => {
  const params = new URLSearchParams();
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (action) params.append('action', action);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (premises_filter) params.append('premises_filter', premises_filter);
  if (late_filter) params.append('late_filter', 'true');
  if (include_hidden) params.append('include_hidden', 'true');
  if (meta_only) params.append('meta_only', 'true');
  params.append('is_intrusion', String(is_intrusion));
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  if (Array.isArray(selected_fields) && selected_fields.length) {
    params.append('selected_fields', selected_fields.join(','));
  }
  if (Array.isArray(selected_person_ids) && selected_person_ids.length) {
    params.append('selected_person_ids', selected_person_ids.join(','));
  }
  if (include_all_employees_presence) params.append('include_all_employees_presence', 'true');
  if (days != null) params.append('days', String(days));
  const response = await authenticatedFetch(`/auth/admin/attendance/export?${params.toString()}`);
  return response.json();
};

// Attendance API
export const markAttendance = async ({
  person_id,
  action,
  person_name = '',
  employee_code = '',
  timestamp = '',
  remark = '',
  audit_id = '',
  is_possible_match = false,
}) => {
  const response = await authenticatedFetch('/auth/attendance', {
    method: 'POST',
    body: JSON.stringify({
      person_id,
      action,
      person_name: person_name || undefined,
      employee_code: employee_code || undefined,
      timestamp: timestamp || undefined,
      audit_id: audit_id || undefined,
      remark: remark || undefined,
      is_possible_match: Boolean(is_possible_match),
    }),
  });
  return response.json();
};

export const getAttendanceLogs = async ({ person_id = '', person_name = '', action = '', start_time = '', end_time = '', days = 30, limit = 10, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (action) params.append('action', action);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (days != null) params.append('days', String(days));
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const response = await authenticatedFetch(`/auth/admin/attendance/logs?${params.toString()}`);
  return response.json();
};

export const getAttendanceSummary = async ({ person_id = '', person_name = '', action = '', start_time = '', end_time = '', days = 30, limit = 12, offset = 0, premises_filter = '', late_filter = false, sort_order = 'desc', is_intrusion = false, is_vendor = null, is_visitor = null, attendance_sync = 'all' } = {}) => {
  const params = new URLSearchParams();
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (action) params.append('action', action);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (days != null) params.append('days', String(days));
  if (premises_filter) params.append('premises_filter', premises_filter);
  if (late_filter) params.append('late_filter', 'true');
  if (sort_order) params.append('sort_order', sort_order);
  params.append('is_intrusion', String(is_intrusion));
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  if (attendance_sync && attendance_sync !== 'all') params.append('attendance_sync', attendance_sync);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const response = await authenticatedFetch(`/auth/admin/attendance/summary?${params.toString()}`);
  return response.json();
};

export const getAttendanceSummaryCards = async ({ person_id = '', person_name = '', action = '', start_time = '', end_time = '', days = 30, limit = 12, offset = 0, premises_filter = '', late_filter = false, sort_order = 'desc', is_intrusion = false, is_vendor = null, is_visitor = null, attendance_sync = 'all' } = {}) => {
  const params = new URLSearchParams();
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (action) params.append('action', action);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (days != null) params.append('days', String(days));
  if (premises_filter) params.append('premises_filter', premises_filter);
  if (late_filter) params.append('late_filter', 'true');
  if (sort_order) params.append('sort_order', sort_order);
  params.append('is_intrusion', String(is_intrusion));
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  if (attendance_sync && attendance_sync !== 'all') params.append('attendance_sync', attendance_sync);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const response = await authenticatedFetch(`/auth/admin/attendance/summary/cards?${params.toString()}`);
  return response.json();
};

export const getAttendancePersonTimeline = async ({ person_id = '', person_name = '', start_time = '', end_time = '', days = 30, company_id = '', branch_id = '', is_intrusion = false, is_vendor = null, is_visitor = null } = {}) => {
  const params = new URLSearchParams();
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (days != null) params.append('days', String(days));
  if (company_id) params.append('company_id', company_id);
  if (branch_id) params.append('branch_id', branch_id);
  params.append('is_intrusion', String(is_intrusion));
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  const response = await authenticatedFetch(`/auth/admin/attendance/summary/person-timeline?${params.toString()}`);
  return response.json();
};

export const getIntrusionSummary = async ({ person_id = '', person_name = '', action = '', start_time = '', end_time = '', days = 30, limit = 12, offset = 0, premises_filter = '', late_filter = false, sort_order = 'desc' } = {}) => {
  const params = new URLSearchParams();
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (action) params.append('action', action);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (days != null) params.append('days', String(days));
  if (premises_filter) params.append('premises_filter', premises_filter);
  if (late_filter) params.append('late_filter', 'true');
  if (sort_order) params.append('sort_order', sort_order);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const response = await authenticatedFetch(`/auth/admin/attendance/intrusion-summary?${params.toString()}`);
  return response.json();
};

export const getActivityLogGroups = async ({
  start_time = '',
  end_time = '',
  person_id = '',
  person_name = '',
  camera_id = '',
  q = '',
  is_unknown = null,
  status = '',
  is_violation = null,
  limit = 20,
  offset = 0,
  company_id = '',
  branch_id = '',
  is_vendor = null,
  is_visitor = null,
} = {}) => {
  const params = new URLSearchParams();
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (camera_id) params.append('camera_id', camera_id);
  if (q) params.append('q', q);
  if (is_unknown !== null && is_unknown !== undefined) params.append('is_unknown', String(Boolean(is_unknown)));
  if (status) params.append('status', status);
  if (is_violation !== null && is_violation !== undefined) params.append('is_violation', String(Boolean(is_violation)));
  if (company_id) params.append('company_id', company_id);
  if (branch_id) params.append('branch_id', branch_id);
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const response = await authenticatedFetch(`/auth/admin/activity/logs/groups?${params.toString()}`);
  return response.json();
};

export const getActivityLogs = async ({
  start_time = '',
  end_time = '',
  person_id = '',
  person_name = '',
  camera_id = '',
  is_unknown = null,
  status = '',
  limit = 25,
  offset = 0,
  company_id = '',
  branch_id = '',
  is_vendor = null,
  is_visitor = null,
} = {}) => {
  const params = new URLSearchParams();
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (person_id) params.append('person_id', person_id);
  if (person_name) params.append('person_name', person_name);
  if (camera_id) params.append('camera_id', camera_id);
  if (is_unknown !== null && is_unknown !== undefined) params.append('is_unknown', String(Boolean(is_unknown)));
  if (status) params.append('status', status);
  if (company_id) params.append('company_id', company_id);
  if (branch_id) params.append('branch_id', branch_id);
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const response = await authenticatedFetch(`/auth/admin/activity/logs?${params.toString()}`);
  return response.json();
};

export const getActivityGroupTimeline = async (
  groupKey,
  {
    limit = 30,
    offset = 0,
    start_time = '',
    end_time = '',
    company_id = '',
    branch_id = '',
    camera_id = '',
  } = {}
) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (company_id) params.append('company_id', company_id);
  if (branch_id) params.append('branch_id', branch_id);
  if (camera_id) params.append('camera_id', camera_id);
  const response = await authenticatedFetch(`/auth/admin/activity/logs/group/${encodeURIComponent(groupKey)}/timeline?${params.toString()}`);
  return response.json();
};

export const getActivityGroupCameraAnalysis = async (
  groupKey,
  { start_time = '', end_time = '', company_id = '', branch_id = '', camera_id = '' } = {}
) => {
  const params = new URLSearchParams();
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (company_id) params.append('company_id', company_id);
  if (branch_id) params.append('branch_id', branch_id);
  if (camera_id) params.append('camera_id', camera_id);
  const response = await authenticatedFetch(
    `/auth/admin/activity/logs/group/${encodeURIComponent(groupKey)}/camera-analysis?${params.toString()}`
  );
  return response.json();
};

export const getActivityLogDetail = async (activityId) => {
  const response = await authenticatedFetch(`/auth/admin/activity/logs/${encodeURIComponent(activityId)}`);
  return response.json();
};

export const getActivityNames = async ({
  q = '',
  start_time = '',
  end_time = '',
  limit = 100,
  company_id = '',
  branch_id = '',
} = {}) => {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (company_id) params.append('company_id', company_id);
  if (branch_id) params.append('branch_id', branch_id);
  params.append('limit', String(limit));
  const response = await authenticatedFetch(`/auth/admin/activity/names?${params.toString()}`);
  return response.json();
};

export const getActivityCameras = async () => {
  const response = await authenticatedFetch('/activity-camera/cameras');
  return response.json();
};

export const startActivityCameraStream = async (cameraId) => {
  const qs = cameraId ? `?camera_id=${encodeURIComponent(cameraId)}` : '';
  const response = await authenticatedFetch(`/activity-camera/stream/start${qs}`, { method: 'POST' });
  return response.json();
};

export const stopActivityCameraStream = async (cameraId, { force = false } = {}) => {
  const params = new URLSearchParams();
  if (cameraId) params.append('camera_id', cameraId);
  if (force) params.append('force', 'true');
  const qs = params.toString();
  const response = await authenticatedFetch(`/activity-camera/stream/stop${qs ? `?${qs}` : ''}`, { method: 'POST' });
  return response.json();
};

export const getActivityFeatureSettings = async () => {
  const response = await authenticatedFetch('/activity-camera/settings');
  return response.json();
};

export const updateActivityFeatureSettings = async (payload = {}) => {
  const response = await authenticatedFetch('/activity-camera/settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const getAbsentLogs = async ({ start_time = '', end_time = '', days = 30, sort_order = 'desc', limit = 10, offset = 0, person_name = '', is_vendor = null, is_visitor = null } = {}) => {
  const params = new URLSearchParams();
  if (start_time) params.append('start_time', start_time);
  if (end_time) params.append('end_time', end_time);
  if (days != null) params.append('days', String(days));
  if (sort_order) params.append('sort_order', sort_order);
  if (person_name) params.append('person_name', person_name.trim());
  if (is_vendor !== null && is_vendor !== undefined) params.append('is_vendor', String(Boolean(is_vendor)));
  if (is_visitor !== null && is_visitor !== undefined) params.append('is_visitor', String(Boolean(is_visitor)));
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  const response = await authenticatedFetch(`/auth/admin/attendance/absent?${params.toString()}`);
  return response.json();
};

export const exportAbsentEmployees = async ({
  start_time = '',
  end_time = '',
  days = 30,
  sort_order = 'desc',
  company_id = '',
  branch_id = '',
  person_id = '',
  person_name = '',
  is_vendor = null,
  is_visitor = null,
  selected_fields = null,
  export_format = 'csv',
  file_name = 'absent_employees',
} = {}) => {
  const response = await authenticatedFetch('/auth/admin/attendance/absent/export', {
    method: 'POST',
    timeoutMs: 300000,
    body: JSON.stringify({
      start_time,
      end_time,
      days,
      sort_order,
      company_id: company_id || undefined,
      branch_id: branch_id || undefined,
      person_id: person_id || undefined,
      person_name: person_name || undefined,
      is_vendor: is_vendor === null || is_vendor === undefined ? undefined : Boolean(is_vendor),
      is_visitor: is_visitor === null || is_visitor === undefined ? undefined : Boolean(is_visitor),
      selected_fields,
      export_format,
      file_name,
    }),
  });

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition') || response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : null;
  return { blob, filename };
};

// Audit Image to Employee API functions
export const addAuditImageToEmployee = async (auditId, personId, name, employeeCode, companyId, branchId) => {
  const response = await authenticatedFetch('/auth/admin/audit/add-to-employee', {
    method: 'POST',
    body: JSON.stringify({
      audit_id: auditId,
      person_id: personId,
      name: name,
      employee_code: employeeCode,
      company_id: companyId,
      branch_id: branchId
    }),
  });
  return response.json();
};

export const createEmployeeFromAudit = async (auditId, personId, name) => {
  const response = await authenticatedFetch('/auth/admin/audit/create-employee', {
    method: 'POST',
    body: JSON.stringify({
      audit_id: auditId,
      person_id: personId,
      name: name
    }),
  });
  return response.json();
};

export const matchAuditImage = async (auditId, similarityThreshold = null, applyUpdate = true, previewResult = null) => {
  const response = await authenticatedFetch('/auth/admin/audit/match-image', {
    method: 'POST',
    body: JSON.stringify({
      audit_id: auditId,
      similarity_threshold: similarityThreshold ?? undefined,
      apply_update: applyUpdate,
      recognized_name: previewResult?.recognized_name ?? undefined,
      recognized_person_id: previewResult?.recognized_person_id ?? undefined,
      recognized_face_id: previewResult?.recognized_face_id ?? undefined,
      confidence_score: previewResult?.confidence_score ?? undefined,
      confidence_level: previewResult?.confidence_level ?? undefined,
    }),
  });
  return response.json();
};

export const refreshTopMatchers = async (auditId) => {
  const response = await authenticatedFetch('/auth/admin/audit/refresh-top-matchers', {
    method: 'POST',
    body: JSON.stringify({
      audit_id: auditId,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data?.detail;
    const message = typeof detail === 'string'
      ? detail
      : (data?.message || 'Failed to refresh top matchers');
    throw new Error(message);
  }
  return data;
};

export const markAuditAttendance = async ({ audit_id, person_id, person_name = '', employee_code = '', action = 'check_in' }) => {
  const response = await authenticatedFetch('/auth/admin/audit/mark-present', {
    method: 'POST',
    body: JSON.stringify({
      audit_id,
      person_id,
      person_name: person_name || undefined,
      employee_code: employee_code || undefined,
      action,
    }),
  });
  return response.json();
};


// CRM: fetch contacts via backend proxy
export const fetchCrmContacts = async ({ since, until, page = 1, sort = 'name' }) => {
  const filters = {
    filters: {
      payload: [
        {
          attribute_key: 'created_at',
          filter_operator: 'is_greater_than',
          values: [since],
          query_operator: 'and',
          attribute_model: 'standard',
          custom_attribute_type: '',
          statusValues: [],
        },
        {
          attribute_key: 'created_at',
          filter_operator: 'is_less_than',
          values: [until],
          custom_attribute_type: '',
          statusValues: [],
        },
      ],
    },
    actions: [],
    is_actions: false,
  };
  const response = await authenticatedFetch('/integrations/crm/contacts', {
    method: 'POST',
    body: JSON.stringify({ ...filters, page, sort, include_contact_inboxes: false }),
  });
  return response.json();
};

// CRM: search contacts by name via backend proxy
export const searchCrmContacts = async ({ q, page = 1, sort = '-created_at' }) => {
  const params = new URLSearchParams();
  params.append('q', q || '');
  params.append('page', String(page));
  params.append('sort', sort);
  params.append('include_contact_inboxes', 'false');
  const response = await authenticatedFetch(`/integrations/crm/contacts/search?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const getDbTables = async () => {
  const response = await authenticatedFetch('/debug/db-explorer/tables');
  return response.json();
};

export const getDbTableColumns = async (tableName) => {
  const response = await authenticatedFetch(
    `/debug/db-explorer/tables/${encodeURIComponent(tableName)}/columns`
  );
  return response.json();
};

export const queryDbTable = async ({
  table,
  page = 1,
  pageSize = 50,
  filters = [],
  sortBy,
  sortOrder = 'DESC',
  start_date,
  end_date,
  start_time,
  end_time,
}) => {
  const response = await authenticatedFetch('/debug/db-explorer/query', {
    method: 'POST',
    body: JSON.stringify({
      table,
      page,
      pageSize,
      filters,
      sortBy: sortBy || undefined,
      sortOrder,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      start_time: start_time || undefined,
      end_time: end_time || undefined,
    }),
  });
  return response.json();
};

// HRMS: fetch employees via backend proxy
// - sync=false: fetch from local database only
// - sync=true with from_date/to_date: fetch from external API using date range (no sync_token)
// - sync=true without dates: fetch from external API using sync_token
export const fetchHrmsEmployees = async ({
  since,
  until,
  from_date,
  to_date,
  page = 1,
  limit = 30,
  sync = false,
  companyId = null,
  branchId = null,
  deviceId = null,
  apiKeyId = null,
  designation = null,
  department = null,
}) => {
  const params = new URLSearchParams();
  if (since) params.append('since', since);
  if (until) params.append('until', until);
  if (from_date) params.append('from_date', from_date);
  if (to_date) params.append('to_date', to_date);
  params.append('page', String(page));
  params.append('limit', String(limit));
  if (sync) params.append('sync', 'true');  // Add sync parameter
  if (companyId) params.append('company_id', companyId);
  // Only add branch_id if it's a valid non-empty string (null/undefined/empty means show all branches)
  if (branchId && typeof branchId === 'string' && branchId.trim().length > 0) {
    params.append('branch_id', branchId);
  }
  if (deviceId) params.append('device_id', deviceId);
  if (apiKeyId) params.append('api_key_id', apiKeyId);
  if (designation) params.append('designation', designation);
  if (department) params.append('department', department);
  
  const response = await authenticatedFetch(`/integrations/hrms/employees?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

// HRMS: search employees by name via backend proxy
export const searchHrmsEmployees = async ({
  q,
  page = 1,
  limit = 20,
  companyId = null,
  designation = null,
  department = null,
}) => {
  const params = new URLSearchParams();
  params.append('q', q || '');
  params.append('page', String(page));
  params.append('limit', String(limit));
  if (companyId) params.append('company_id', companyId);
  if (designation) params.append('designation', designation);
  if (department) params.append('department', department);
  const response = await authenticatedFetch(`/integrations/hrms/employees/search?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const fetchHrmsDesignations = async ({
  companyId,
  branchId = null,
  q = '',
  includeInactive = false,
  limit = 200,
}) => {
  const params = new URLSearchParams();
  if (companyId) params.append('company_id', companyId);
  if (branchId) params.append('branch_id', branchId);
  if (q) params.append('q', q);
  params.append('include_inactive', includeInactive ? 'true' : 'false');
  params.append('limit', String(limit));
  const response = await authenticatedFetch(`/integrations/hrms/designations?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const fetchHrmsDepartments = async ({
  companyId,
  branchId = null,
  q = '',
  includeInactive = false,
  limit = 200,
}) => {
  const params = new URLSearchParams();
  if (companyId) params.append('company_id', companyId);
  if (branchId) params.append('branch_id', branchId);
  if (q) params.append('q', q);
  params.append('include_inactive', includeInactive ? 'true' : 'false');
  params.append('limit', String(limit));
  const response = await authenticatedFetch(`/integrations/hrms/departments?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

export const updateHrmsEmployeeName = async (employeeId, name) => {
  const response = await authenticatedFetch(`/integrations/hrms/employees/${encodeURIComponent(employeeId)}/name`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
  return response.json();
};

// HRMS: get last sync status from hrms_sync_logs table
export const getHrmsSyncStatus = async (companyId = null, branchId = null) => {
  const params = new URLSearchParams();
  if (companyId) {
    params.append('company_id', companyId);
  }
  if (branchId && branchId.trim()) {
    params.append('branch_id', branchId);
  }
  const queryString = params.toString();
  const url = `/integrations/hrms/sync-status${queryString ? `?${queryString}` : ''}`;
  
  const response = await authenticatedFetch(url, {
    method: 'GET',
  });
  return response.json();
};

// (Batch recognition: removed; using test endpoint below)

// Test flow: multi-face detect, crop, and match
export const recognizeTest = async (dataUrl, camera = 'IN', options = {}) => {
  const token = getStoredToken();
  if (!token) throw new Error('No authentication token found');
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  // Capture timestamp when image was taken for accurate timing metrics
  const capturedAtMs = Date.now();
  const { skipAuditLog = false, similarityThreshold = undefined } = options || {};
  try {
    const response = await fetch(buildApiUrl('/test/recognize'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // Hint backend/audit about camera direction
        'X-Camera': String(camera || '').toUpperCase(),
      },
      body: JSON.stringify({ 
        image_base64: dataUrl,
        captured_at_ms: capturedAtMs,
        skip_audit_log: skipAuditLog,
        similarity_threshold: similarityThreshold,
      }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  } catch (e) {
    clearTimeout(timeoutId);
    if (e.name === 'AbortError') throw new Error('Request timeout. Please try again.');
    throw e;
  }
};

// Check if a person ID exists (optionally including deleted records)
export const checkPersonExists = async (personId, includeDeleted = true) => {
  const params = new URLSearchParams();
  params.append('include_deleted', includeDeleted ? 'true' : 'false');
  const response = await authenticatedFetch(`/faces/person/${encodeURIComponent(personId)}/exists?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

// API Keys management functions
export const getApiKeys = async (limit = 10, offset = 0, filters = {}) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  if (filters?.companyId) params.append('company_id', String(filters.companyId));
  if (filters?.branchId) params.append('branch_id', String(filters.branchId));
  if (filters?.isActive !== undefined && filters?.isActive !== null) {
    params.append('is_active', String(Boolean(filters.isActive)));
  }
  const response = await authenticatedFetch(`/auth/api-keys?${params.toString()}`);
  return response.json();
};

export const createApiKey = async ({
  keyName,
  expiresDays = null,
  companyId = null,
  branchId = null,
  deviceName = null,
  baseUrl = null,
}) => {
  const response = await authenticatedFetch('/auth/api-keys', {
    method: 'POST',
    body: JSON.stringify({
      key_name: keyName,
      expires_days: expiresDays,
      company_id: companyId,
      branch_id: branchId,
      device_name: deviceName,
      base_url: baseUrl,
    }),
  });
  return response.json();
};

export const deleteApiKey = async (keyId) => {
  const response = await authenticatedFetch(`/auth/api-keys/${keyId}`, {
    method: 'DELETE',
  });
  return response;
};

export const activateApiKey = async (keyId) => {
  const response = await authenticatedFetch(`/auth/api-keys/${keyId}/activate`, {
    method: 'POST',
  });
  return response.json();
};

export const deactivateApiKey = async (keyId) => {
  const response = await authenticatedFetch(`/auth/api-keys/${keyId}/deactivate`, {
    method: 'POST',
  });
  return response.json();
};

// Dashboard API functions
export const getTotalRegistrations = async () => {
  const response = await authenticatedFetch('/dashboard/total-registrations');
  return response.json();
};

export const getTotalRecognitions = async () => {
  const response = await authenticatedFetch('/dashboard/total-recognition');
  return response.json();
};

export const getRecognitionPerDay = async (startDate = null, endDate = null, singleDate = null) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (singleDate) params.append('single_date', singleDate);

  const endpoint = params.toString()
    ? `/dashboard/user-recognition-pie?${params.toString()}`
    : '/dashboard/user-recognition-pie';

  const response = await authenticatedFetch(endpoint);
  return response.json();
};

export const getEntryTraffic = async (date = null) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  const tzOffset = new Date().getTimezoneOffset();
  params.append('tz_offset_minutes', tzOffset);

  const endpoint = params.toString()
    ? `/dashboard/entry-traffic?${params.toString()}`
    : '/dashboard/entry-traffic';

  const response = await authenticatedFetch(endpoint);
  return response.json();
};

export const getTotalActiveEmployees = async () => {
  const response = await authenticatedFetch('/dashboard/total-active-employees');
  return response.json();
};

export const getLateEntryTime = async () => {
  const response = await authenticatedFetch('/settings/late-entry-time');
  return response.json();
};

/**
 * Update late entry settings. Pass a string (HH:MM) for time-only, or an object:
 * { late_entry_time?, late_entry_activate? } — at least one key required by API.
 */
export const updateLateEntryTime = async (lateEntryTimeOrPayload) => {
  const body =
    typeof lateEntryTimeOrPayload === 'string'
      ? { late_entry_time: lateEntryTimeOrPayload }
      : { ...lateEntryTimeOrPayload };
  const response = await authenticatedFetch('/settings/late-entry-time', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getStoredToken()}`,
    },
    body: JSON.stringify(body),
  });
  return response.json();
};

// Camera stream metadata
export const getCameraStreams = async () => {
  const response = await authenticatedFetch('/camera-streams');
  return response.json();
};

export const listCustomWebviews = async () => {
  const response = await authenticatedFetch('/camera-streams/custom-webviews');
  return response.json();
};

export const getCustomWebview = async (webviewId) => {
  const response = await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(webviewId)}`);
  return response.json();
};

export const createCustomWebview = async (payload) => {
  const response = await authenticatedFetch('/camera-streams/custom-webviews', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const updateCustomWebview = async (webviewId, payload) => {
  const response = await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(webviewId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const deleteCustomWebview = async (webviewId) => {
  const response = await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(webviewId)}`, {
    method: 'DELETE',
  });
  return response.json();
};

// Add a new camera stream
export const addCameraStream = async (cameraData) => {
  const response = await authenticatedFetch('/camera-streams/cameras', {
    method: 'POST',
    body: JSON.stringify(cameraData),
  });
  return response.json();
};

// Get available RTSP streams for selection (from external service)
export const getAvailableStreams = async () => {
  const response = await authenticatedFetch('/camera-streams/available');
  return response.json();
};

// Save a selected stream to database
export const saveSelectedStream = async (url, label, cameraType) => {
  const response = await authenticatedFetch('/camera-streams/save-selected', {
    method: 'POST',
    body: JSON.stringify({
      url,
      label,
      camera_type: cameraType,
    }),
  });
  return response.json();
};

// Deactivate a camera (remove from database but keep in available list)
export const deactivateCamera = async (cameraId) => {
  const response = await authenticatedFetch(`/camera-streams/deactivate/${cameraId}`, {
    method: 'DELETE',
  });
  return response.json();
};

// Delete a camera completely from database
export const deleteCamera = async (cameraId) => {
  const response = await authenticatedFetch(`/camera-streams/${cameraId}`, {
    method: 'DELETE',
  });
  return response.json();
};

// Update camera stream information
export const updateCameraStream = async (cameraId, updateData) => {
  const response = await authenticatedFetch(`/camera-streams/${cameraId}/activate`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
  return response.json();
};

// Edit camera details and stream URLs/labels
export const editCameraDetails = async (cameraId, updateData) => {
  const response = await authenticatedFetch(`/camera-streams/cameras/${cameraId}/edit`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
  return response.json();
};

export const getCameraStreamById = async (cameraId) => {
  const response = await authenticatedFetch(`/camera-streams/${cameraId}`);
  return response.json();
};

export const getCameraHealth = async (cameraId) => {
  const response = await authenticatedFetch(`/camera-streams/${cameraId}/health`);
  return response.json();
};

export const getCameraHealthPreview = async (cameraId, streamUrl) => {
  const query = streamUrl ? `?stream_url=${encodeURIComponent(streamUrl)}` : '';
  const response = await authenticatedFetch(`/camera-streams/${cameraId}/health-preview${query}`);
  return response.json();
};

/**
 * SSE overlay stream for IN/OUT live preview (ROI + tracks + trails over go2rtc video).
 */
export const getLivePreviewOverlayUrl = (cameraType, fps = 10) => {
  const token = getStoredToken() || '';
  const params = new URLSearchParams({
    camera_type: String(cameraType || '').toUpperCase(),
    fps: String(fps),
  });
  if (token) params.set('token', token);
  params.set('t', String(Date.now()));
  return buildApiUrl(`/camera-streams/preview-overlay-stream?${params.toString()}`);
};

/**
 * go2rtc MSE WebSocket for IN/OUT ROI Manage Preview (DeepStream RTSP-out, boxes already in video).
 */
export const getDeepStreamPreviewMseUrl = (cameraId) => {
  const token = getStoredToken() || '';
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  const id = encodeURIComponent(String(cameraId || ''));
  if (!id) return null;
  return buildApiUrl(`/camera-streams/cameras/${id}/ds-preview-mse?${params.toString()}`);
};

/**
 * go2rtc MSE WebSocket URL for configured camera live preview (plain RTSP, no overlays).
 */
export const getConfiguredCameraLivePreviewMseUrl = (
  cameraId,
  { streamId, streamUrl } = {}
) => {
  const token = getStoredToken() || '';
  const params = new URLSearchParams();
  if (streamId) params.set('stream_id', String(streamId));
  if (streamUrl) params.set('stream_url', String(streamUrl));
  if (token) params.set('token', token);
  const id = encodeURIComponent(String(cameraId || ''));
  return buildApiUrl(`/camera-streams/cameras/${id}/configured-preview-mse?${params.toString()}`);
};

/** Stop DeepStream IN/OUT overlay stream when the live preview modal closes. */
export const stopDeepStreamPreviewWatch = async (cameraType) => {
  const camera = cameraType ? String(cameraType).toUpperCase() : undefined;
  const response = await authenticatedFetch('/camera-streams/preview-watch/stop', {
    method: 'POST',
    body: JSON.stringify(camera ? { camera } : {}),
  });
  return response.json();
};

export const getCameraRtspStatus = async (cameraId, streamUrl) => {
  const response = await authenticatedFetch(`/camera-streams/${cameraId}/status`, {
    method: 'POST',
    body: JSON.stringify(streamUrl ? { stream_url: streamUrl } : {}),
  });
  return response.json();
};

// IP speakers (admin UI)
export const getSpeakers = async () => {
  const response = await authenticatedFetch('/speakers');
  return response.json();
};

export const getSpeakerById = async (speakerId) => {
  const response = await authenticatedFetch(`/speakers/${encodeURIComponent(speakerId)}`);
  return response.json();
};

export const createSpeaker = async (payload) => {
  const response = await authenticatedFetch('/speakers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const updateSpeaker = async (speakerId, payload) => {
  const response = await authenticatedFetch(`/speakers/${encodeURIComponent(speakerId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const deleteSpeaker = async (speakerId) => {
  const response = await authenticatedFetch(`/speakers/${encodeURIComponent(speakerId)}`, {
    method: 'DELETE',
  });
  return response.json();
};

export const checkSpeakerStatus = async (speakerId) => {
  const response = await authenticatedFetch(`/speakers/${encodeURIComponent(speakerId)}/status`, {
    method: 'POST',
  });
  return response.json();
};

export const testSpeakerPlayback = async (speakerId) => {
  const response = await authenticatedFetch(`/speakers/${encodeURIComponent(speakerId)}/test`, {
    method: 'POST',
  });
  return response.json();
};

export const getSpeakerRouteConfig = async () => {
  const response = await authenticatedFetch('/speakers/route-config');
  return response.json();
};

export const putSpeakerRouteConfig = async (payload) => {
  const response = await authenticatedFetch('/speakers/route-config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const getHooterConfig = async () => {
  const response = await authenticatedFetch('/hooter/config', { method: 'GET' });
  return response.json();
};

export const updateHooterConfig = async (payload) => {
  const response = await authenticatedFetch('/hooter/config', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const testHooterPlayback = async (host = '') => {
  const body = host ? { host } : {};
  const response = await authenticatedFetch('/hooter/test', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.json();
};

// Fetch a single-frame snapshot for a specific camera stream URL
export const getRtspSnapshot = async (cameraIdentifier, streamUrl) => {
  // No camera yet (Add Camera Test): probe by URL only
  if (!cameraIdentifier) {
    const response = await authenticatedFetch('/camera-streams/rtsp-snapshot', {
      method: 'POST',
      body: JSON.stringify({ stream_url: streamUrl }),
    });
    return response.json();
  }
  const response = await authenticatedFetch(`/camera-streams/cameras/${encodeURIComponent(cameraIdentifier)}/rtsp-snapshot`, {
    method: 'POST',
    body: JSON.stringify({ stream_url: streamUrl }),
  });
  return response.json();
};

export const checkWebViewHealth = async () => {
  const response = await authenticatedFetch('/health/websocket');
  return response.json();
};

export const checkDetectAppHealth = async () => {
  const response = await authenticatedFetch('/health/detect-app');
  return response.json();
};

// ROI Configuration API functions

/**
 * Get ROI configuration for a specific camera
 * @param {string} cameraType - "IN" or "OUT"
 * @param {{ cameraId?: string }} [options]
 * @returns {Promise<object>} - ROI configuration { camera_type, roi: {x, y, w, h}, updated_at, updated_by }
 */
export const getROIConfig = async (cameraType, { cameraId } = {}) => {
  const qs = cameraId ? `?camera_id=${encodeURIComponent(cameraId)}` : '';
  const response = await authenticatedFetch(`/settings/roi/${cameraType.toUpperCase()}${qs}`);
  return response.json();
};

/**
 * List all active IN/OUT cameras with per-camera ROI
 * @param {{ cameraType?: string }} [options]
 * @returns {Promise<object>} - { cameras, total, updated_at }
 */
export const getROICameras = async ({ cameraType } = {}) => {
  const qs = cameraType ? `?camera_type=${encodeURIComponent(cameraType.toUpperCase())}` : '';
  const response = await authenticatedFetch(`/settings/roi/cameras${qs}`);
  return response.json();
};

/**
 * Save ROI configuration for a specific camera
 * @param {string} cameraType - "IN" or "OUT"
 * @param {object} roiData - ROI data { camera_id?, rect?, number_of_lane?, crop_configuration? }
 * @returns {Promise<object>} - Saved ROI configuration
 */
export const saveROIConfig = async (cameraType, roiData) => {
  const response = await authenticatedFetch(`/settings/roi/${cameraType.toUpperCase()}`, {
    method: 'PUT',
    body: JSON.stringify(roiData),
  });
  return response.json();
};

/**
 * Delete ROI configuration for a specific camera
 * @param {string} cameraType - "IN" or "OUT"
 * @param {{ cameraId?: string }} [options]
 * @returns {Promise<object>} - Deletion result
 */
export const deleteROIConfig = async (cameraType, { cameraId } = {}) => {
  const qs = cameraId ? `?camera_id=${encodeURIComponent(cameraId)}` : '';
  const response = await authenticatedFetch(`/settings/roi/${cameraType.toUpperCase()}${qs}`, {
    method: 'DELETE',
  });
  return response.json();
};

/**
 * Get all ROI configurations (for detect app integration)
 * Returns format compatible with roi_config.json: { IN: { rect: [...] }, OUT: { rect: [...] } }
 */
export const getAllROIConfigs = async () => {
  const response = await authenticatedFetch('/settings/roi');
  return response.json();
};

/**
 * Get frame from detect app for ROI drawing (via health-preview)
 * @param {string} cameraType - "IN" or "OUT"
 * @param {{ cameraId?: string }} [options]
 * @returns {Promise<object>} - Frame data with image as base64
 */
export const getFrameForROI = async (cameraType, { cameraId } = {}) => {
  const qs = cameraId ? `?camera_id=${encodeURIComponent(cameraId)}` : '';
  const response = await authenticatedFetch(`/settings/roi/${cameraType.toUpperCase()}/frame${qs}`);
  return response.json();
};

export const getDetectionSettings = async ({ includeHealth = false } = {}) => {
  const query = includeHealth ? '?include_health=true' : '';
  const response = await authenticatedFetch(`/settings/detection${query}`);
  return response.json();
};

export const updateDetectionSettings = async (payload = {}) => {
  const response = await authenticatedFetch('/settings/detection', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.json();
};

export const getRecognitionCropThreshold = async () => {
  const response = await authenticatedFetch('/recognition/crop-threshold');
  return response.json();
};

export const updateRecognitionCropThreshold = async (payload = {}) => {
  const response = await authenticatedFetch('/recognition/crop-threshold', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return response.json();
};

// Kivo Integration API functions
export const getKivoConfig = async () => {
  const response = await authenticatedFetch('/integrations/kivo/config');
  return response.json();
};

export const getKivoProfiles = async (configId = null) => {
  const query = configId ? `?config_id=${encodeURIComponent(configId)}` : '';
  const response = await authenticatedFetch(`/integrations/kivo/profiles${query}`);
  return response.json();
};

export const getKivoBranches = async (companyId = null) => {
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : '';
  const response = await authenticatedFetch(`/integrations/kivo/branches${query}`);
  return response.json();
};

/**
 * Re-sync branches for a company from Kivo and persist
 */
export const refreshKivoCompanyBranches = async (companyId) => {
  if (!companyId) {
    throw new Error('company_id is required');
  }
  const query = `?company_id=${encodeURIComponent(companyId)}`;
  const response = await authenticatedFetch(`/integrations/kivo/branches/refresh${query}`, {
    method: 'POST',
  });
  return response.json();
};

export const updateKivoConfig = async ({ accessToken, companyId = null, companyName = null, baseUrl = null, isDefault = false }) => {
  const response = await authenticatedFetch('/integrations/kivo/config', {
    method: 'POST',
    body: JSON.stringify({
      access_token: accessToken,
      company_id: companyId,
      company_name: companyName,
      base_url: baseUrl,
      is_default: isDefault,
    }),
  });
  return response.json();
};

export const deleteKivoConfig = async () => {
  const response = await authenticatedFetch('/integrations/kivo/config', {
    method: 'DELETE',
  });
  return response.json();
};
export const getCompanyAttendanceCounts = async (startDate, endDate) => {
  const response = await authenticatedFetch('/integrations/kivo/company-attendance-counts', {
    method: 'POST',
    body: JSON.stringify({
      start_date: startDate,
      end_date: endDate,
    }),
  });
  return response.json();
};

export const createSyncJob = async (startDate, endDate, companies) => {
  const response = await authenticatedFetch('/integrations/kivo/create-sync-job', {
    method: 'POST',
    body: JSON.stringify({
      start_date: startDate,
      end_date: endDate,
      companies: companies,
    }),
  });
  return response.json();
};

export const getSyncHistory = async () => {
  const response = await authenticatedFetch('/integrations/kivo/sync-history', {
    method: 'GET',
  });
  return response.json();
};

export const getKivoAttendanceCompanies = async () => {
  const response = await authenticatedFetch('/integrations/kivo/attendance-companies', {
    method: 'GET',
  });
  return response.json();
};

export const getKivoExportSchedules = async () => {
  const response = await authenticatedFetch('/integrations/kivo/schedules', {
    method: 'GET',
  });
  return response.json();
};

export const upsertKivoExportSchedule = async (companyId, { intervalMinutes, isEnabled, companyName }) => {
  const response = await authenticatedFetch(`/integrations/kivo/schedules/${encodeURIComponent(companyId)}`, {
    method: 'PUT',
    body: JSON.stringify({
      interval_minutes: intervalMinutes,
      is_enabled: isEnabled,
      company_name: companyName,
    }),
  });
  return response.json();
};

export const deleteKivoExportSchedule = async (companyId) => {
  const response = await authenticatedFetch(`/integrations/kivo/schedules/${encodeURIComponent(companyId)}`, {
    method: 'DELETE',
  });
  return response.json();
};

export const getBranchDevices = async (branchId) => {
  const response = await authenticatedFetch(`/branches/${branchId}/devices`);
  return response.json();
};

export const refreshKivoStatus = async (syncKivoId) => {
  const response = await authenticatedFetch(`/integrations/kivo/refresh?sync_kivo_id=${syncKivoId}`, {
    method: 'GET',
  });
  return response.json();
};

// Intrusion Accounts Management API functions
export const getIntrusionAccounts = async (limit = 100, offset = 0, includeInactive = false) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    include_inactive: includeInactive.toString(),
  });
  const response = await authenticatedFetch(`/auth/admin/accounts?${params.toString()}`);
  return response.json();
};

// Intrusion Accounts Management API functions
export const createAccount = async (accountName) => {
  const response = await authenticatedFetch('/auth/admin/accounts', {
    method: 'POST',
    body: JSON.stringify({ account_name: accountName }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create account' }));
    throw new Error(error.detail || 'Failed to create account');
  }
  return response.json();
};

export const updateIntrusionAccount = async (companyId, companyData) => {
  const response = await authenticatedFetch(`/auth/admin/accounts/${companyId}`, {
    method: 'PUT',
    body: JSON.stringify(companyData),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update account' }));
    throw new Error(error.detail || 'Failed to update account');
  }
  return response.json();
};


// Fetch list of companies (legacy function - keeping for backward compatibility)
export const fetchCompanies = async (limit = 100, offset = 0, includeInactive = false) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    include_inactive: includeInactive.toString(),
  });
  const response = await authenticatedFetch(`/companies?${params.toString()}`);
  return response.json();
};

/** Kivo-config-shaped company list. Uses /companies when Kivo integration is off. */
export const getCompanyPickerConfig = async (permissions) => {
  if (shouldUseKivoConfigSource(permissions)) {
    return getKivoConfig();
  }
  const data = await fetchCompanies(200, 0, false);
  const companies = Array.isArray(data?.companies) ? data.companies : [];
  const configs = companies
    .filter((row) => row?.company_id)
    .map((row) => ({
      config_id: row.company_id,
      company_id: row.company_id,
      company_name: row.company_name || 'Default Company',
      is_default: false,
    }));
  return {
    configs,
    default_config_id: configs[0]?.config_id || '',
    total: data?.total ?? configs.length,
  };
};

export const listLocalBranches = async ({ companyId = null, limit = 200, offset = 0, includeInactive = false } = {}) => {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    include_inactive: String(includeInactive),
  });
  if (companyId) params.append('company_id', companyId);
  const response = await authenticatedFetch(`/branches?${params.toString()}`);
  return response.json();
};

export const createLocalBranch = async ({ branchId, companyId, branchName, branchAddress }) => {
  const response = await authenticatedFetch('/branches', {
    method: 'POST',
    body: JSON.stringify({
      branch_id: branchId,
      company_id: companyId,
      branch_name: branchName,
      branch_address: branchAddress,
    }),
  });
  return response.json();
};

// Get API key by company, branch, and device affiliation
export const getApiKeyByAffiliation = async (companyId, branchId = null, deviceName = null) => {
  const params = new URLSearchParams({ company_id: companyId });
  if (branchId) params.append('branch_id', branchId);
  if (deviceName) params.append('device_name', deviceName);
  
  const response = await authenticatedFetch(`/api-keys/by-affiliation?${params.toString()}`);
  return response.json();
};

// Upload voice file for an employee (checkin or checkout)
export const uploadEmployeeVoice = async (employeeId, voiceType, audioBlob) => {
  const token = getStoredToken();
  if (!token) throw new Error('No authentication token found');

  const formData = new FormData();
  formData.append('voice_file', audioBlob, `${voiceType}_voice.wav`);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(buildApiUrl(`/integrations/hrms/employees/${employeeId}/voice/${voiceType}`), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Do NOT set Content-Type; browser will set multipart boundary
      },
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
};

export const listAIVoiceApiKeys = async (limit = 20, refresh = true) => {
  const params = new URLSearchParams({
    limit: limit.toString(),
    refresh: refresh ? 'true' : 'false',
    provider: 'cartesia',
    credential_type: 'voice_generation',
  });
  const response = await authenticatedFetch(`/integrations/hrms/voice-generation/api-keys?${params.toString()}`);
  return response.json();
};

/** Most recently used saved key (backend sorts by last_used, then created_at). */
export const getDefaultAIVoiceKeySelection = (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return null;
  const row = keys.find((k) => k?.credential_id) || keys[0];
  if (!row?.credential_id) return null;
  return {
    credentialId: row.credential_id,
    hint: row.credential_hint || '',
  };
};

export const saveAIVoiceApiKey = async (apiKey) => {
  const response = await authenticatedFetch('/integrations/hrms/voice-generation/api-keys', {
    method: 'POST',
    body: JSON.stringify({
      api_key: apiKey,
      provider: 'cartesia',
      credential_type: 'voice_generation',
    }),
  });
  return response.json();
};

export const generateEmployeeAIVoice = async (employeeId, voiceType, payload = {}) => {
  const response = await authenticatedFetch(`/integrations/hrms/employees/${employeeId}/voice/${voiceType}/generate-ai`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.json();
};

// Generic API call with authentication
export const authenticatedFetch = async (endpoint, options = {}) => {
  let token = getStoredToken();

  if (!token) {
    token = await waitForToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
  }

  const { timeoutMs, ...fetchOptions } = options;
  const requestUrl = buildApiUrl(endpoint);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs ?? timeout);

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
    signal: controller.signal,
  };

  try {
    const response = await fetch(requestUrl, {
      ...defaultOptions,
      ...fetchOptions,
    });

    clearTimeout(timeoutId);

    const initialError = response.status === 401 || response.status === 403
      ? await readErrorResponse(response)
      : null;

    if (initialError?.code === 'ACCOUNT_INACTIVE') {
      endBrowserSession('ACCOUNT_INACTIVE', initialError.message);
      throw createResponseError(response, initialError);
    }

    if (response.status === 401) {
      let refreshedAccessToken;
      try {
        // Another request may already have refreshed while this request was in
        // flight. Reuse that token instead of rotating the refresh token twice.
        const latestStoredToken = getStoredToken();
        refreshedAccessToken = latestStoredToken && latestStoredToken !== token
          ? latestStoredToken
          : await refreshStoredTokens();
      } catch (refreshError) {
        const reason = refreshError.code === 'ACCOUNT_INACTIVE'
          ? 'ACCOUNT_INACTIVE'
          : refreshError.code === 'SESSION_REVOKED'
            ? 'SESSION_REVOKED'
            : 'SESSION_EXPIRED';
        endBrowserSession(reason, refreshError.message);
        throw refreshError;
      }

      const retryResponse = await fetch(requestUrl, {
        ...defaultOptions,
        ...fetchOptions,
        headers: {
          ...defaultOptions.headers,
          'Authorization': `Bearer ${refreshedAccessToken}`,
        },
      });

      if (retryResponse.status === 401 || retryResponse.status === 403) {
        const retryError = await readErrorResponse(retryResponse);
        if (retryError.code === 'ACCOUNT_INACTIVE') {
          endBrowserSession('ACCOUNT_INACTIVE', retryError.message);
        } else if (retryResponse.status === 401) {
          endBrowserSession(
            retryError.code === 'SESSION_REVOKED' ? 'SESSION_REVOKED' : 'SESSION_EXPIRED',
            retryError.message
          );
        }
        throw createResponseError(retryResponse, retryError);
      }

      if (!retryResponse.ok) {
        const retryError = await readErrorResponse(retryResponse);
        throw createResponseError(retryResponse, retryError);
      }

      return retryResponse;
    }

    if (!response.ok) {
      const errorData = await readErrorResponse(response);
      throw createResponseError(response, errorData);
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    throw error;
  }
};

export const getNotificationSettings = async () => {
  const response = await authenticatedFetch('/auth/user/notification-settings');
  if (!response.ok) {
    throw new Error('Failed to fetch notification settings');
  }
  return response.json();
};

export const getNotificationUnreadCount = async () => {
  const response = await authenticatedFetch('/auth/admin/notifications/unread-count');
  if (!response.ok) {
    throw new Error('Failed to fetch notification unread count');
  }
  return response.json();
};

export const updateNotificationSettings = async (payload = {}) => {
  const response = await authenticatedFetch('/auth/user/notification-settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to update notification settings');
  }
  return response.json();
};

export const getUserSettings = async () => {
  const response = await authenticatedFetch('/auth/user/settings');
  if (!response.ok) {
    throw new Error('Failed to fetch user settings');
  }
  return response.json();
};

export const updateUserSettings = async (payload = {}) => {
  const response = await authenticatedFetch('/auth/user/settings', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to update user settings');
  }
  return response.json();
};

export const updateHrmsEmployeeValidity = async (employeeId, validFrom = null, validTill = null, validityTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
  const response = await authenticatedFetch(`/integrations/hrms/employees/${encodeURIComponent(employeeId)}/validity`, {
    method: 'PATCH',
    body: JSON.stringify({
      valid_from: validFrom,
      valid_till: validTill,
      validity_timezone: validityTimezone
     }),
  });
  return response.json();
};

// Alert Rules API
export const getAlertRules = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
  const queryString = params.toString();
  const url = queryString ? `/alert-rules?${queryString}` : '/alert-rules';
  const response = await authenticatedFetch(url, { method: 'GET' });
  return response.json();
};

export const createAlertRule = async (ruleData) => {
  const response = await authenticatedFetch('/alert-rules', {
    method: 'POST',
    body: JSON.stringify(ruleData),
  });
  return response.json();
};

export const updateAlertRule = async (ruleId, ruleData) => {
  const response = await authenticatedFetch(`/alert-rules/${encodeURIComponent(ruleId)}`, {
    method: 'PUT',
    body: JSON.stringify(ruleData),
  });
  return response.json();
};

export const toggleAlertRule = async (ruleId, isActive) => {
  const response = await authenticatedFetch(`/alert-rules/${encodeURIComponent(ruleId)}/toggle`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  });
  return response.json();
};

export const deleteAlertRule = async (ruleId) => {
  const response = await authenticatedFetch(`/alert-rules/${encodeURIComponent(ruleId)}`, {
    method: 'DELETE',
  });
  return response.json();
};

export const getAlertSounds = async () => {
  const response = await authenticatedFetch('/alert-sounds', { method: 'GET' });
  return response.json();
};

export const fetchAlertSoundBlob = async (filename) => {
  const response = await authenticatedFetch(
    `/alert-sounds/${encodeURIComponent(filename)}`,
    { method: 'GET' }
  );
  return response.blob();
};

export const playAlertSoundOnSpeaker = async (filename, speakerId = '') => {
  const params = new URLSearchParams();
  if (speakerId) params.set('speaker_id', speakerId);
  const query = params.toString();
  const url = query
    ? `/alert-sounds/${encodeURIComponent(filename)}/play?${query}`
    : `/alert-sounds/${encodeURIComponent(filename)}/play`;
  const response = await authenticatedFetch(url, { method: 'POST' });
  return response.json();
};

export const getWhatsAppTemplates = async ({ page = 1, perPage = 100, status = 'approved' } = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
    status,
  });
  const response = await authenticatedFetch(`/integrations/crm/whatsapp/templates?${params.toString()}`, {
    method: 'GET',
  });
  return response.json();
};

